import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { ScriptMarkupBreadcrumb, getCloudinarySrcSet, getCloudinaryUrl } from 'components/rich';
import useDataContext from 'data/context/data';
import useClaimedCoupon from 'ecommerce/context/claimedCoupon';
import { isFreeShippingOnly } from 'ecommerce/lib/coupon';

import BudgetTiles, { BUDGET_TILES } from '../components/gifts/budgetTiles';
import {
  BudgetGrid,
  BudgetHero,
  BudgetStickyBar,
  UseCases,
  cardId,
  loadBudgetContent,
} from '../components/gifts/budgetSections';
import CouponStrip from '../components/gifts/couponStrip';
import FinishingTouches from '../components/gifts/finishingTouches';
import LandingEditorial from '../components/landing/editorial';
import { HERO_IMAGE_SIZES } from '../components/landing/hero';
import { resolveIdeas } from '../components/gifts/giftIdea';
import LandingStory from '../components/landing/story';
import RecipientList from '../components/gifts/recipientList';
import { ctaTracker, landingImage, productImage } from '../components/landing/shared';
import { Head, Layout } from '../components/layout';
import useInView from '../lib/useInView';
import useScrolledPast from '../lib/useScrolledPast';
import SETTINGS from '../settings';

// Gifts under $20 (docs/gifts-page.md#budget-pages). Explicit page file, so it wins over the
// category route in [...params].js. The product list comes from the category spec, title,
// meta, H1 and editorial from content/categories/gifts-under-20.md, the rest is design copy.
const CATEGORY_ID = 'gifts-under-20';
const track = ctaTracker('gifts_budget_page', { budget: 20 });

const HERO_IMAGES = {
  block: productImage('glossy-prints/1'),
  couple: productImage('classic-prints/3'),
  tiny: productImage('tiny-square-prints/1'),
};

// The use case each product is merchandised for on this page.
const CARD_BADGES = {
  'square-prints': 'STOCKING STUFFER',
  'metal-desk-prints': 'FOR THEIR DESK',
  'classic-prints': 'FITS ANY FRAME',
  'glossy-prints': 'SECRET SANTA',
  'softcover-books': 'FOR GRANDPARENTS',
  'wood-block': 'TEACHER GIFT',
};

// Recipients jump to the product card that fits them best.
const RECIPIENTS = [
  {
    name: 'Stockings',
    hint: 'small enough to fit, good to keep',
    target: cardId('square-prints'),
    image: productImage('classic-prints/6'),
  },
  {
    name: 'Teachers',
    hint: 'a class photo for their desk',
    target: cardId('wood-block'),
    image: productImage('classic-prints/7'),
  },
  {
    name: 'Secret Santa',
    hint: 'a safe bet for any name you draw',
    target: cardId('glossy-prints'),
    image: productImage('classic-prints/1'),
  },
  {
    name: 'Coworkers',
    hint: 'the team trip, in prints',
    target: cardId('classic-prints'),
    image: productImage('classic-prints/2'),
  },
  {
    name: 'Grandparents',
    hint: 'a little book to show off',
    target: cardId('softcover-books'),
    image: landingImage('sc-stack-wood'),
  },
  {
    name: 'Best friend',
    hint: 'the whole summer, in squares',
    target: cardId('square-prints'),
    image: productImage('square-prints/1'),
  },
];

const IDEAS = [
  {
    id: 'idea-stockings',
    recipient: 'For stockings',
    title: 'Stocking stuffers that get kept',
    body: 'A set of Square Prints, each one slipped into a Square Envelope. Flat, small and easy to hand out on the day.',
    short: 'Square Prints, each in its own Square Envelope.',
    productNames: ['Square Prints', 'Envelopes'],
    products: ['square-prints', 'square-envelopes'],
    image: productImage('square-prints/3'),
  },
  {
    id: 'idea-teacher',
    recipient: 'For the teacher',
    title: 'A desk photo with its own stand',
    body: 'A 4x4 Metal Desk Print comes with a wooden block stand, so it’s on their desk the minute it’s unwrapped.',
    short: 'A 4x4 Metal Desk Print with a wooden block stand.',
    productNames: ['Metal Desk Print'],
    products: ['metal-desk-prints'],
    image: productImage('metal-desk-prints/3'),
  },
  {
    id: 'idea-office',
    recipient: 'For the office',
    title: 'Prints from the team trip',
    body: 'Classic Prints come in sets of 20. One set is enough for everyone in the office to take a photo home.',
    short: 'Sets of 20, one for everyone in the office.',
    productNames: ['Classic Prints'],
    products: ['classic-prints'],
    image: productImage('classic-prints/6'),
  },
];

const BUDGETS = [BUDGET_TILES.under50, BUDGET_TILES.giftCards, BUDGET_TILES.gifts];

const GiftsUnder20Page = ({ content, cardMeta }) => {
  const dataContext = useDataContext();
  const claimedCoupon = useClaimedCoupon();
  const { previewSummary, summary } = claimedCoupon;
  const heroRef = useRef();
  const gridEndRef = useRef();
  const heroInView = useInView(heroRef);
  const pastGrid = useScrolledPast(gridEndRef);

  const category = dataContext.getCategory(CATEGORY_ID);
  const gifts = dataContext.getCategory('gifts');
  const breadcrumb = [
    { name: 'Home', url: '/' },
    { name: gifts.name, url: gifts.url },
    { name: 'Under $20' },
  ];
  const products = category.data
    .flatMap((section) => section.products)
    .map((product) => ({ product, meta: cardMeta[product.id], badge: CARD_BADGES[product.id] }));

  return (
    <>
      <Head
        title={category.seoTitle ?? category.name}
        description={category.seoDescription ?? category.shortDescription}
        image={content.seoUrl || getCloudinaryUrl({ imageKey: category.image })}
        heroImage={getCloudinaryUrl({
          imageKey: HERO_IMAGES.block.imageKey,
          prefix: HERO_IMAGES.block.prefix,
        })}
        heroImageSrcSet={getCloudinarySrcSet(HERO_IMAGES.block)}
        heroImageSizes={HERO_IMAGE_SIZES}
      />
      <ScriptMarkupBreadcrumb items={breadcrumb} host={SETTINGS.HOST} />

      <Layout>
        <div className="lnd gft">
          <CouponStrip claimedCoupon={claimedCoupon} />

          <BudgetHero
            ref={heroRef}
            category={category}
            breadcrumb={breadcrumb}
            lead={{
              desktop:
                'Personalized gifts under $20 for everyone on your list. Stocking stuffers, teacher thank-yous and Secret Santa, all printed from your photos.',
              phone: 'Personalized gifts under $20 for everyone on your list.',
            }}
            giftCount={products.length}
            images={HERO_IMAGES}
            tag={{ to: 'To Ms. Rivera', note: ['Thank you for', 'this year! Leo'] }}
            track={track}
          />

          <RecipientList
            eyebrow="Small gifts, long list"
            title="Who’s on your list?"
            aside="Every gift here is $20 or less. Cover the teacher, the office and every stocking without blowing the budget."
            recipients={RECIPIENTS}
            track={track}
          />

          <BudgetGrid
            ref={gridEndRef}
            eyebrow="All gifts under $20"
            title="Six gifts, all $20 or less"
            products={products}
            columns={3}
            previewSummary={previewSummary}
            track={track}
          />

          <UseCases
            eyebrow="Three ways to give"
            title="One order covers the whole list"
            aside="Ideas for the small gifts that add up. Stockings, the teacher, the office swap."
            ideas={resolveIdeas(IDEAS, dataContext, previewSummary)}
            track={track}
          />

          <LandingStory
            image={landingImage('sc-stack-blue')}
            quote="I love that the photo books are small enough to keep in your handbag, because you know grandmas love to show off photos of their grandkids when they’re out and about!"
            author="@littlehiccups"
            product="Softcover Photo Books"
          />

          <BudgetTiles
            title="Bigger gift, or let them choose?"
            tiles={BUDGETS}
            couponClaimed={Boolean(summary) && !isFreeShippingOnly(summary)}
            track={track}
          />

          <FinishingTouches track={track} />

          <LandingEditorial
            eyebrow="About gifts under $20"
            title="Small photo gifts that don’t feel small"
            html={content.extraDescription}
          />

          <BudgetStickyBar
            visible={!heroInView}
            pastGrid={pastGrid}
            products={products}
            previewSummary={previewSummary}
            track={track}
          />
        </div>
      </Layout>
    </>
  );
};

GiftsUnder20Page.getInitialProps = () => loadBudgetContent(CATEGORY_ID);

GiftsUnder20Page.propTypes = {
  content: PropTypes.object.isRequired,
  cardMeta: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default GiftsUnder20Page;
